import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { initialState } from './constants';
import {
  SchedulerSidebarContextData,
  SchedulerSidebarData,
  SchedulerSidebarProviderProps,
} from './types';
import { useGetUsersSchedule } from '@services/schedules/internal/get';
import { useGetSchedulesConflicts } from '@services/schedules/conflicts';
import { useGetEquipmentSchedulesConflicts } from '@services/schedules/equipmentConflicts';
import { ScheduleConflictEntry } from '@services/schedules/conflicts/types';
import { useSchedulerContext } from '..';
import {
  getCrewMembersFromForeman,
  getEquipmentMembersFromForeman,
  mapCrewMembersToSchedulerSidebarCrewMembers,
  mapEquipmentMembersToSchedulerSidebarCrewMembers,
  mapResponseScheduleEquipmentToForm,
  mapResponseScheduleMembersToForm,
  excludeFieldUserFromCrew,
} from './helpers';
import { useDropdownsContext } from '@contexts/dropdowns';
import { SchedulerData } from '../types';

const SchedulerSidebarContext = createContext<SchedulerSidebarContextData | Record<string, never>>(
  {},
);

export const SchedulerSidebarProvider = ({ children }: SchedulerSidebarProviderProps) => {
  const [state, setState] = useState<SchedulerSidebarData>(initialState);

  const {
    active: { schedule, foremanChanged },
    setState: setSchedulerState,
  } = useSchedulerContext();

  const { fieldUsers } = useDropdownsContext();

  const id = schedule?.id !== 'new' ? schedule?.id : undefined;

  const { data, isLoading, isPlaceholderData, isFetching } = useGetUsersSchedule(id);

  const { data: conflictsData } = useGetSchedulesConflicts({
    startsAt: schedule?.startsAt,
    endsAt: schedule?.endsAt,
    scheduleId: id,
    taskType: schedule?.task?.foremanSpecialty?.value,
    enabled: Boolean(schedule?.task?.id && schedule?.startsAt && schedule?.endsAt),
  });

  const conflictsByUserId = useMemo(
    () =>
      (conflictsData?.data || []).reduce<Record<string, ScheduleConflictEntry[]>>((acc, el) => {
        acc[el.userId] = el.conflicts;
        return acc;
      }, {}),
    [conflictsData],
  );

  const { data: equipmentConflictsData } = useGetEquipmentSchedulesConflicts({
    startsAt: schedule?.startsAt,
    endsAt: schedule?.endsAt,
    scheduleId: id,
    taskType: schedule?.task?.foremanSpecialty?.value,
    enabled: Boolean(schedule?.task?.id && schedule?.startsAt && schedule?.endsAt),
  });

  const conflictsByAssetId = useMemo(
    () =>
      (equipmentConflictsData?.data || []).reduce<Record<string, ScheduleConflictEntry[]>>(
        (acc, el) => {
          acc[el.assetId] = el.conflicts;
          return acc;
        },
        {},
      ),
    [equipmentConflictsData],
  );

  const populatedForemanId = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!schedule.id) return;
    if (schedule.id !== 'new') {
      populatedForemanId.current = undefined;
      if (state.populateDefaultCrewAndEquipment)
        setState((prev) => ({ ...prev, populateDefaultCrewAndEquipment: false }));
      return;
    }
    if (state.populateDefaultCrewAndEquipment) populatedForemanId.current = undefined;

    const foremanId = schedule.foreman?.id;
    if (!foremanId || !fieldUsers?.options?.length) return;
    if (populatedForemanId.current === foremanId) return;

    populatedForemanId.current = foremanId;
    populateDefaults(foremanId);
  }, [
    schedule.id,
    schedule.foreman?.id,
    fieldUsers,
    state.populateDefaultCrewAndEquipment,
    state.defaultCrew,
    state.defaultEquipment,
  ]);

  const populateDefaults = (foremanId: string) => {
    const defaultCrewMembers = getCrewMembersFromForeman(foremanId, fieldUsers);
    const defaultEquipmentMembers = getEquipmentMembersFromForeman(foremanId, fieldUsers);
    setState((prev) => ({
      ...prev,
      populateDefaultCrewAndEquipment: false,
      ...(prev.defaultCrew && {
        defaultCrew: true,
        crewMembers: mapCrewMembersToSchedulerSidebarCrewMembers(defaultCrewMembers),
      }),
      ...(prev.defaultEquipment && {
        defaultEquipment: true,
        equipmentMembers: mapEquipmentMembersToSchedulerSidebarCrewMembers(defaultEquipmentMembers),
      }),
    }));
  };

  useEffect(() => {
    if (state.currentSchedule?.id || !schedule.id || schedule.id === 'new') return;
    if (isLoading || isPlaceholderData || isFetching) return;
    if (!data?.data || data.data.currentSchedule.id !== schedule.id) return;

    const { currentSchedule, relatedMultischedules } = data.data;
    const {
      defaultCrew,
      defaultCrewChanged,
      defaultEquipment,
      defaultEquipmentChanged,
      scheduleMembers,
      scheduleAssets,
      task: { requirements },
    } = currentSchedule;

    const newForemanId = schedule.foreman?.id;
    const rescheduledToDifferentForeman =
      !!newForemanId && newForemanId !== currentSchedule.foreman?.id;

    const shouldPopulateDefaultCrew = rescheduledToDifferentForeman && currentSchedule.defaultCrew;
    const shouldPopulateDefaultEquipment =
      rescheduledToDifferentForeman && currentSchedule.defaultEquipment;

    if (
      (shouldPopulateDefaultCrew || shouldPopulateDefaultEquipment) &&
      !fieldUsers?.options?.length
    )
      return;

    const newForemanDefaultCrew = getCrewMembersFromForeman(newForemanId, fieldUsers);
    const newForemanDefaultEquipment = getEquipmentMembersFromForeman(newForemanId, fieldUsers);
    const crewChangeScope = relatedMultischedules ? 'currentAndFuture' : 'currentOnly';

    const crewMembersFromResponse = mapResponseScheduleMembersToForm(scheduleMembers);
    const crewMembers = shouldPopulateDefaultCrew
      ? mapCrewMembersToSchedulerSidebarCrewMembers(newForemanDefaultCrew)
      : excludeFieldUserFromCrew(crewMembersFromResponse, newForemanId);

    const equipmentMembersFromResponse = mapResponseScheduleEquipmentToForm(scheduleAssets);
    const equipmentMembers = shouldPopulateDefaultEquipment
      ? mapEquipmentMembersToSchedulerSidebarCrewMembers(newForemanDefaultEquipment)
      : equipmentMembersFromResponse;

    setState((prev) => ({
      ...prev,
      confirm: {
        crewAssignment: {
          open: rescheduledToDifferentForeman && !currentSchedule.defaultCrew,
          keepCurrentCrew: true,
        },
        crewChanged: {
          open: defaultCrew && defaultCrewChanged && !rescheduledToDifferentForeman,
          keep: true,
        },
        equipmentAssignment: {
          open: rescheduledToDifferentForeman && !currentSchedule.defaultEquipment,
          keepCurrentEquipment: true,
        },
        equipmentChanged: {
          open: defaultEquipment && defaultEquipmentChanged && !rescheduledToDifferentForeman,
          keep: true,
        },
      },
      defaultCrew,
      defaultCrewChanged,
      defaultEquipment,
      defaultEquipmentChanged,
      currentSchedule,
      relatedMultischedules,
      crewMembers,
      equipmentMembers,
      crewChangeScope,
      requirements,
    }));
  }, [
    data?.data,
    isLoading,
    isPlaceholderData,
    isFetching,
    schedule.id,
    schedule.foreman?.id,
    state.currentSchedule?.id,
    fieldUsers,
  ]);

  useEffect(() => {
    setState((prev) => ({
      ...prev,
      disabled:
        prev.confirm.crewAssignment.open ||
        prev.confirm.crewChanged.open ||
        prev.confirm.equipmentAssignment.open ||
        prev.confirm.equipmentChanged.open,
    }));
  }, [
    state.confirm.crewAssignment.open,
    state.confirm.crewChanged.open,
    state.confirm.equipmentAssignment.open,
    state.confirm.equipmentChanged.open,
  ]);

  useEffect(() => {
    if (!foremanChanged) return;

    const { defaultCrew, defaultEquipment } = state;
    const newForemanId = schedule.foreman?.id;
    const movedBackToCurrentScheduleForeman = newForemanId === state.currentSchedule?.foreman?.id;
    const shouldHandleCrewAndEquipment = Boolean(foremanChanged && schedule.foreman?.id);

    const confirmCrewAssignmentOpen =
      !defaultCrew && shouldHandleCrewAndEquipment && !movedBackToCurrentScheduleForeman;
    const shouldPopulateDefaultCrew = defaultCrew && shouldHandleCrewAndEquipment;

    const confirmEquipmentAssignmentOpen =
      !defaultEquipment && shouldHandleCrewAndEquipment && !movedBackToCurrentScheduleForeman;
    const shouldPopulateDefaultEquipment = defaultEquipment && shouldHandleCrewAndEquipment;

    if (
      (shouldPopulateDefaultCrew || shouldPopulateDefaultEquipment) &&
      !fieldUsers?.options?.length
    )
      return;

    const newForemanDefaultCrew = getCrewMembersFromForeman(newForemanId, fieldUsers);
    const newForemanDefaultEquipment = getEquipmentMembersFromForeman(newForemanId, fieldUsers);

    setSchedulerState((prev: SchedulerData) => ({
      ...prev,
      active: {
        ...prev.active,
        foremanChanged: false,
      },
    }));

    setState((prev) => ({
      ...prev,
      crewMembers: shouldPopulateDefaultCrew
        ? mapCrewMembersToSchedulerSidebarCrewMembers(newForemanDefaultCrew)
        : excludeFieldUserFromCrew(prev.crewMembers, newForemanId),
      equipmentMembers: shouldPopulateDefaultEquipment
        ? mapEquipmentMembersToSchedulerSidebarCrewMembers(newForemanDefaultEquipment)
        : prev.equipmentMembers,
      confirm: {
        ...prev.confirm,
        crewAssignment: { ...prev.confirm.crewAssignment, open: confirmCrewAssignmentOpen },
        equipmentAssignment: {
          ...prev.confirm.equipmentAssignment,
          open: confirmEquipmentAssignmentOpen,
        },
      },
    }));
  }, [schedule.id, schedule.foreman?.id, foremanChanged, state.defaultCrew, fieldUsers]);

  return (
    <SchedulerSidebarContext.Provider
      value={{ ...state, setState, conflictsByUserId, conflictsByAssetId }}>
      {children}
    </SchedulerSidebarContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useSchedulerSidebar = () => useContext(SchedulerSidebarContext);
